import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import GridDraggableItem from 'components/structure-page/2d/grid-draggable-item';
import { AirHandlerTypeCodeType } from 'data/types';
import { bumpValidSystemsRetry } from 'features/systems';
import useAirHandlerAvailabilityStatus from 'hooks/use-air-handler-availability-status';
import useMatchingAirHandlerMetrics from 'hooks/use-matching-air-handler-metrics';
import { ComponentProps, useCallback, useMemo } from 'react';
import {
  getAirHandlerTypeCodesForDragType,
  getAirHandlerTypeDisplayLabel
} from 'utils/air-handler-input-helpers';
import { hasNoSystemsForConfiguration } from 'utils/configuration-system-availability';
import {
  AIR_HANDLER_UNAVAILABLE_REASON,
  getLevelRestrictionReasons
} from 'utils/air-handler-restrictions';
import { everyZoneHasAirHandler } from 'utils/configurations';
import {
  isDuctedOnlySystemPreference,
  isHybridOnlySystemPreference
} from 'utils/project';
import {
  getAirHandlerTypeCodeFromDragType,
  getRestrictedAirHandlerTypesForSection
} from 'utils/structure';

const exposedAirHandlers = [
  {
    label: 'High Wall',
    image: '/images/air-handlers/high-wall.svg',
    description: 'Wall-mounted near ceiling, ideal for cooling',
    type: 'new-air-handler-high-wall' as const
  },
  {
    label: 'Low Wall',
    image: '/images/air-handlers/low-wall.svg',
    description: 'Mounted near floor level, better for heating',
    type: 'new-air-handler-low-wall' as const
  },
  {
    label: 'Floor Ceiling Console',
    image: '/images/air-handlers/floor-ceiling-console.svg',
    description: 'Versatile floor or ceiling mount options',
    type: 'new-air-handler-floor-ceiling' as const
  }
];

const concealedAirHandlers = [
  {
    label: 'Ceiling Cassette',
    image: '/images/air-handlers/ceiling-cassette.svg',
    description: 'Recessed in ceiling, 360° air distribution',
    type: 'new-air-handler-ceiling-cassette' as const
  },
  {
    label: 'Ceiling Cassette Slim Fit',
    image: '/images/air-handlers/ceiling-cassette.svg',
    description: 'Slimmer profile that fits between 16" joists',
    type: 'new-air-handler-ceiling-cassette-slim-fit' as const
  },
  {
    label: 'Concealed Duct',
    image: '/images/air-handlers/concealed-duct.svg',
    description: 'Hidden in ceiling with ductwork',
    type: 'new-air-handler-concealed-duct' as const
  }
];

// AHA-6784: only rendered on the hybrid and ducted paths. No BMAH-specific
// asset exists; the concealed-duct SVG is the accepted fallback.
const ductedAirHandlers = [
  {
    label: getAirHandlerTypeDisplayLabel('ducted'),
    image: '/images/air-handlers/concealed-duct.svg',
    description: 'Central air handler with ductwork for multiple rooms',
    type: 'new-air-handler-ducted' as const
  }
];

type AirHandlerListProps = {
  disabled?: boolean;
};

export default function AirHandlerList({
  disabled = false
}: AirHandlerListProps) {
  const dispatch = useAppDispatch();
  const { estimatingStructure, activeLevelIndex } = useAppSelector(
    (state) => state.ui
  );
  const { structure } = useAppSelector((state) => state.structure);
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const activeConfigurationId = configurations[activeConfig]?.uniqueId;
  const availabilityStatus = useAirHandlerAvailabilityStatus(
    activeConfigurationId
  );
  const isRefreshing = availabilityStatus === 'loading';
  // Same retry contract as Choose Systems: bump the nonce and let the
  // watcher clear the failure when a request actually succeeds.
  const handleRetryAvailability = useCallback(() => {
    if (!activeConfigurationId) return;
    dispatch(bumpValidSystemsRetry({ configId: activeConfigurationId }));
  }, [dispatch, activeConfigurationId]);
  const systemPreference = useAppSelector(
    (state) =>
      state.project.projectSettings.find(
        (ps) => ps.code === 'system_preference'
      )?.value
  );
  const isHybrid = isHybridOnlySystemPreference(systemPreference);
  const isDuctedOnly = isDuctedOnlySystemPreference(systemPreference);
  const { availableAirHandlers, metrics, isLoading } =
    useMatchingAirHandlerMetrics();
  // Nothing in the pool at all: placing heads cannot lead to a system, so
  // the step is blocked here (and at Next) until the zones change.
  const noSystemsForConfiguration = hasNoSystemsForConfiguration(
    configurations[activeConfig]
  );

  const openZoneIds = useMemo(() => {
    const activeConfiguration = configurations[activeConfig];
    if (!activeConfiguration) return new Set<string>();
    const ids = new Set<string>();
    activeConfiguration.condensers.forEach((condenser) => {
      condenser.zones.forEach((zone) => {
        if (!zone.airHandler) ids.add(zone.uniqueId);
      });
    });
    return ids;
  }, [configurations, activeConfig]);

  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    Object.entries(availableAirHandlers).forEach(([zoneId, codes]) => {
      if (!openZoneIds.has(zoneId)) return;
      codes.forEach((code) => set.add(code));
    });
    return set;
  }, [availableAirHandlers, openZoneIds]);
  // Known from the pool state, not from the shape of the data: an empty pool
  // and a pool that has not arrived both look like {} otherwise.
  const hasQueryRun = metrics !== null && !isLoading;

  const isAirHandlerTypeUnavailable = useMemo(() => {
    if (!hasQueryRun) return () => false;
    if (availableTypes.size === 0) return () => true;
    return (dragType: string): boolean => {
      const codes = getAirHandlerTypeCodesForDragType(dragType);
      if (codes.length === 0) return false;
      return !codes.some((c) => availableTypes.has(c));
    };
  }, [hasQueryRun, availableTypes]);

  const currentLevel = structure[activeLevelIndex];

  const isAirHandlerTypeRestrictedForLevel = useMemo(() => {
    if (!currentLevel) return () => false;

    const allSections = currentLevel.sections;
    if (allSections.length === 0) return () => false;

    return (dragType: string): boolean => {
      const airHandlerTypeCode = getAirHandlerTypeCodeFromDragType(dragType);
      if (!airHandlerTypeCode) return false;

      return allSections.every((section) => {
        const sectionSpaceIds = section.spaces.map((sp) => sp.uniqueId);
        if (sectionSpaceIds.length === 0) return true;

        const restrictedTypes = getRestrictedAirHandlerTypesForSection(
          sectionSpaceIds,
          structure
        );

        return restrictedTypes.includes(
          airHandlerTypeCode as AirHandlerTypeCodeType
        );
      });
    };
  }, [currentLevel, structure]);

  const allZonesHaveAirHandler = useMemo(
    () => everyZoneHasAirHandler(configurations[activeConfig]),
    [configurations, activeConfig]
  );

  const hardDisabled =
    disabled ||
    isRefreshing ||
    estimatingStructure ||
    allZonesHaveAirHandler ||
    noSystemsForConfiguration;

  const buildWarningReason = (dragType: string): string | undefined => {
    const reasons: string[] = [];
    const code = getAirHandlerTypeCodeFromDragType(dragType);

    if (code && currentLevel && isAirHandlerTypeRestrictedForLevel(dragType)) {
      reasons.push(
        ...getLevelRestrictionReasons(code, currentLevel, structure)
      );
    }

    if (isAirHandlerTypeUnavailable(dragType)) {
      reasons.push(AIR_HANDLER_UNAVAILABLE_REASON);
    }

    return reasons.length > 0 ? reasons.join(' ') : undefined;
  };

  const renderHandlerGroup = (
    handlers: readonly {
      label: string;
      image: string;
      description: string;
      type: string;
    }[]
  ) =>
    handlers.map((handler) => {
      const warning = hardDisabled
        ? undefined
        : buildWarningReason(handler.type);
      return (
        <div key={handler.label} className='flex flex-col gap-2'>
          <GridDraggableItem
            id={handler.type}
            label={handler.label}
            type={
              handler.type as ComponentProps<typeof GridDraggableItem>['type']
            }
            disabled={hardDisabled}
            warning={warning}
            color='green'
            image={handler.image}
            description={handler.description}
            size='large'
          />
        </div>
      );
    });

  return (
    <div className='flex flex-col gap-3'>
      {isRefreshing && !allZonesHaveAirHandler && (
        <div className='flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800'>
          <svg
            className='h-4 w-4 animate-spin'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 512 512'
          >
            <path
              fill='currentColor'
              className='opacity-40'
              d='M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z'
            />
            <path
              fill='currentColor'
              d='M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z'
            />
          </svg>
          <span>Updating air handler availability…</span>
        </div>
      )}
      {noSystemsForConfiguration && (
        <div
          role='alert'
          className='flex flex-col gap-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'
        >
          <span className='font-semibold'>
            No systems can serve this configuration as drawn.
          </span>
          <span>
            No air handler and condenser combination covers these zones (0 valid
            systems). Split or rebalance the zones, move spaces between them, or
            change a zone&apos;s heating or cooling requirement; availability is
            re-checked as you edit.
          </span>
        </div>
      )}
      {availabilityStatus === 'failed' && !allZonesHaveAirHandler && (
        <div
          role='alert'
          className='flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'
        >
          <span>Air handler availability could not be loaded.</span>
          <button
            type='button'
            onClick={handleRetryAvailability}
            className='shrink-0 font-semibold underline hover:text-red-900'
          >
            Retry
          </button>
        </div>
      )}
      {!isDuctedOnly && (
        <>
          <div className='flex items-center gap-1'>
            <Squares2X2Icon className='h-6 w-6' />
            <span className='text-lg font-semibold'>Exposed Air Handlers</span>
          </div>
          <div className='flex flex-col gap-3'>
            {renderHandlerGroup(exposedAirHandlers)}
          </div>
          <div className='flex items-center gap-1'>
            <Squares2X2Icon className='h-6 w-6' />
            <span className='text-lg font-semibold'>
              Concealed Air Handlers
            </span>
          </div>
          <div className='flex flex-col gap-3'>
            {renderHandlerGroup(concealedAirHandlers)}
          </div>
        </>
      )}
      {(isHybrid || isDuctedOnly) && (
        <>
          <div className='flex items-center gap-1'>
            <Squares2X2Icon className='h-6 w-6' />
            <span className='text-lg font-semibold'>Ducted</span>
          </div>
          <div className='flex flex-col gap-3'>
            {renderHandlerGroup(ductedAirHandlers)}
          </div>
        </>
      )}
    </div>
  );
}
